import { z } from 'zod';

export const DashboardRange = z.enum(['7d', '30d', '90d']);
export type DashboardRange = z.infer<typeof DashboardRange>;

export const DashboardSummaryQuery = z.object({
  range: DashboardRange.default('7d'),
});
export type DashboardSummaryQuery = z.infer<typeof DashboardSummaryQuery>;

const HourlyPoint = z.object({
  hour: z.iso.datetime(),
  count: z.number().int().nonnegative(),
});

const DailyPoint = z.object({
  date: z.string(), // YYYY-MM-DD
  count: z.number().int().nonnegative(),
});

const DailyCostPoint = z.object({
  date: z.string(),
  cost: z.number().nonnegative(),
});

const ErroringChannelItem = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  consecutiveErrors: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  lastFetchedAt: z.iso.datetime().nullable(),
});

const TopChannelItem = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  importedCount: z.number().int().nonnegative(),
});

const ByModelItem = z.object({
  model: z.string(),
  costUsd: z.number().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  calls: z.number().int().nonnegative(),
});

const TopCategoryItem = z.object({
  slug: z.string(),
  name: z.string(),
  count: z.number().int().nonnegative(),
});

const ByCityItem = z.object({
  cityId: z.uuid(),
  cityName: z.string(),
  count: z.number().int().nonnegative(),
});

const TopEventItem = z.object({
  id: z.uuid(),
  title: z.string(),
  attendanceCount: z.number().int().nonnegative(),
  cityName: z.string(),
});

export const DashboardSummary = z.object({
  range: DashboardRange,
  generatedAt: z.iso.datetime(),

  moderation: z.object({
    pendingCount: z.number().int().nonnegative(),
    extractedCount: z.number().int().nonnegative(),
    oldestPendingAgeMs: z.number().int().nonnegative().nullable(),
    incomingPerHour24h: z.array(HourlyPoint),
    funnel: z.object({
      pending: z.number().int().nonnegative(),
      extracted: z.number().int().nonnegative(),
      ignored: z.number().int().nonnegative(),
      imported: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
    }),
  }),

  channels: z.object({
    active: z.number().int().nonnegative(),
    inactive: z.number().int().nonnegative(),
    erroring: z.array(ErroringChannelItem),
    topByImports: z.array(TopChannelItem),
  }),

  llm: z.object({
    totalCostUsd: z.number().nonnegative(),
    totalInputTokens: z.number().int().nonnegative(),
    totalOutputTokens: z.number().int().nonnegative(),
    avgCostPerMessage: z.number().nonnegative(),
    byModel: z.array(ByModelItem),
    costPerDay: z.array(DailyCostPoint),
  }),

  events: z.object({
    byStatus: z.object({
      draft: z.number().int().nonnegative(),
      published: z.number().int().nonnegative(),
      archived: z.number().int().nonnegative(),
    }),
    upcomingByDay: z.array(DailyPoint),
    upcomingByCity: z.array(ByCityItem),
    topCategories: z.array(TopCategoryItem),
    importedSharePct: z.number().min(0).max(100),
  }),

  audience: z.object({
    totalUsers: z.number().int().nonnegative(),
    newUsersInRange: z.number().int().nonnegative(),
    dau: z.number().int().nonnegative(),
    wau: z.number().int().nonnegative(),
    mau: z.number().int().nonnegative(),
    premiumSharePct: z.number().min(0).max(100),
    byCity: z.array(ByCityItem),
    topEvents: z.array(TopEventItem),
  }),

  activity: z.object({
    totals: z.object({
      registrations: z.number().int().nonnegative(),
      events: z.number().int().nonnegative(),
      participations: z.number().int().nonnegative(),
      photos: z.number().int().nonnegative(),
      photoTags: z.number().int().nonnegative(),
      messages: z.number().int().nonnegative(),
    }),
    daily: z.array(
      z.object({
        date: z.string(), // YYYY-MM-DD
        registrations: z.number().int().nonnegative(),
        events: z.number().int().nonnegative(),
        participations: z.number().int().nonnegative(),
        photos: z.number().int().nonnegative(),
        photoTags: z.number().int().nonnegative(),
        messages: z.number().int().nonnegative(),
      }),
    ),
  }),
});
export type DashboardSummary = z.infer<typeof DashboardSummary>;
